/**
 * PictureImg — drop-in replacement for <img> that serves a modern WebP
 * to browsers that support it, with the original JPEG/PNG as a graceful
 * fallback via the standard <picture> element.
 *
 * We rely on the upload-side optimiser's filename convention: every
 * `.jpg` / `.jpeg` / `.png` file living under `/api/uploads/…` has a
 * `.webp` sibling of identical dimensions.  That means we can derive
 * the WebP URL by pure string replacement — no extra API calls, no
 * component-level knowledge of the srcset dictionary.
 *
 * External URLs (YouTube thumbnails, hero images from other CDNs) and
 * anything that isn't a JPEG or PNG are passed through unchanged as a
 * plain <img>.  Fully backwards-compatible.
 */
import { useMemo, useState } from 'react';

const RE = /\.(jpe?g|png)(\?.*)?$/i;

function toWebp(src) {
    if (!src || typeof src !== 'string') return null;
    if (!RE.test(src)) return null;
    return src.replace(RE, '.webp$2');
}

export default function PictureImg({ src, alt = '', className, style, loading = 'lazy', decoding = 'async', draggable = 'false', ...rest }) {
    const webp = useMemo(() => toWebp(src), [src]);

    // Lazy-loaded images (the default -- artwork cards, journal, etc.) are
    // fetched on the browser's own schedule, independent of the .reveal
    // scroll-fade on their surrounding container. Without this, a slow
    // network means the container finishes fading in before the image
    // itself has downloaded, so it just pops in abruptly once it lands.
    // Fade the image itself in on load so it never does a hard cut.
    // Eager images (the hero) are deliberately excluded -- that one is
    // meant to appear as fast as possible, not fade in on its own.
    const isLazy = loading === 'lazy';
    const [loaded, setLoaded] = useState(false);
    const loadedStyle = isLazy
        ? { ...style, opacity: loaded ? 1 : 0, transition: 'opacity 0.6s ease-out' }
        : style;
    const onLoad = isLazy ? () => setLoaded(true) : undefined;

    if (!webp) {
        // No convertible extension — external URL, SVG, GIF, etc.
        return (
            <img
                src={src}
                alt={alt}
                className={className}
                style={loadedStyle}
                loading={loading}
                decoding={decoding}
                draggable={draggable}
                onLoad={onLoad}
                {...rest}
            />
        );
    }

    return (
        <picture>
            <source type="image/webp" srcSet={webp} />
            <img
                src={src}
                alt={alt}
                className={className}
                style={loadedStyle}
                loading={loading}
                decoding={decoding}
                draggable={draggable}
                onLoad={onLoad}
                {...rest}
            />
        </picture>
    );
}
